/*
 * PaceCerto — página do comparador (/pages/comparar).
 * Sem ?modelos: monta os 3 espaços a partir da comparação salva.
 * Com ?modelos=a--b--c: tabela no computador e leitura por linha no celular.
 * Portado de comparar.ts, TabelaComparar.tsx, CompararMobile.tsx e SeletorModelo.tsx (Lovable).
 */
(() => {
  function iniciar() {
    const raiz = document.querySelector('[data-pc-comparar]');
    if (!raiz || raiz.dataset.pronto) return;
    raiz.dataset.pronto = '1';

    const PC = window.PaceCerto;
    const { comparar, escapar, formatMoeda, formatRitmo, reguaHTML, nomeCurto } = PC;
    const LIMITE = PC.limite;
    const TITULO_ORIGINAL = document.title;
    const ICONE_X = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12"/></svg>';
    const CORES_MODELO = ['var(--pc-preto)', 'var(--pc-musgo)', 'var(--pc-modelo-3)'];

    const resultado = raiz.querySelector('[data-resultado]');
    const slots = raiz.querySelector('[data-slots]');
    const acoes = raiz.querySelector('[data-acoes]');

    let catalogo = [];
    let catalogoPronto = false;
    let erroCatalogo = false;
    let somenteDiferencas = false; // tabela (computador)
    let soDiferencas = true; // celular

    /* ------------------------------------------------------ linhas (comparar.ts) */

    const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    const t = (v) => {
      const texto = String(v ?? '').trim();
      return texto === '' ? null : texto;
    };
    const ordenarNumeracoes = (lista) =>
      [...new Set(lista.map(String))].sort((a, b) => Number(a) - Number(b) || a.localeCompare(b, 'pt-BR'));

    const LINHAS_TABELA = [
      {
        chave: 'preco',
        rotulo: 'Preço',
        otimo: 'menor',
        rotuloDestaque: 'mais barato',
        numero: (p) => n(p.preco) || null,
        texto: (p) => (p.preco > 0 ? formatMoeda(p.preco) : null),
      },
      {
        chave: 'uso',
        rotulo: 'Categoria de uso',
        otimo: null,
        numero: () => null,
        texto: (p) => t(p.categoria) ?? t(p.specs.uso),
      },
      {
        chave: 'ritmo',
        rotulo: 'Ritmo desejado',
        tipo: 'pacecard',
        otimo: null,
        numero: () => null,
        texto: (p) =>
          p.specs.ritmo_min !== null && p.specs.ritmo_max !== null
            ? `${formatRitmo(p.specs.ritmo_min)}–${formatRitmo(p.specs.ritmo_max)}/km`
            : null,
      },
      {
        chave: 'peso',
        rotulo: 'Peso',
        otimo: 'menor',
        rotuloDestaque: 'mais leve',
        numero: (p) => n(p.specs.peso_g),
        texto: (p) => (n(p.specs.peso_g) === null ? null : `${p.specs.peso_g} g · tamanho de referência não informado`),
      },
      {
        chave: 'stack',
        rotulo: 'Stack (calcanhar/antepé)',
        otimo: null,
        numero: () => null,
        texto: (p) => {
          const calcanhar = n(p.specs.stack_calcanhar);
          const antepe = n(p.specs.stack_antepe);
          if (calcanhar === null && antepe === null) return null;
          return `${calcanhar ?? '—'}/${antepe ?? '—'} mm`;
        },
      },
      {
        chave: 'drop',
        rotulo: 'Drop',
        otimo: null,
        numero: () => null,
        texto: (p) => (n(p.specs.drop) === null ? null : `${p.specs.drop} mm`),
      },
      { chave: 'placa', rotulo: 'Placa', otimo: null, numero: () => null, texto: (p) => t(p.specs.placa) },
      { chave: 'espuma', rotulo: 'Espuma', otimo: null, numero: () => null, texto: (p) => t(p.specs.espuma) },
      { chave: 'pisada', rotulo: 'Pisada', otimo: null, numero: () => null, texto: (p) => t(p.specs.pisada) },
      {
        chave: 'vida_util',
        rotulo: 'Vida útil',
        otimo: 'maior',
        rotuloDestaque: 'dura mais',
        numero: (p) => n(p.specs.vida_util_km),
        texto: (p) => (n(p.specs.vida_util_km) === null ? null : `${p.specs.vida_util_km} km`),
      },
      { chave: 'nivel', rotulo: 'Nível', otimo: null, numero: () => null, texto: (p) => t(p.specs.nivel) },
      {
        chave: 'numeracoes',
        rotulo: 'Numerações disponíveis',
        otimo: null,
        numero: () => null,
        texto: (p) => {
          const base = p.numeracoes.length > 0 ? p.numeracoes : p.numeracoesTodas;
          const lista = ordenarNumeracoes(base.filter(Boolean));
          return lista.length > 0 ? lista.join(' · ') : null;
        },
      },
    ];

    const LINHA_CORES = {
      chave: 'cores',
      rotulo: 'Cores disponíveis',
      otimo: 'maior',
      rotuloDestaque: 'mais cores',
      numero: (p) => (p.cores.length > 0 ? p.cores.length : null),
      texto: (p) => (p.cores.length > 0 ? `${p.cores.length} ${p.cores.length === 1 ? 'cor' : 'cores'}` : null),
    };

    function melhoresIndices(linha, produtos) {
      if (!linha.otimo) return [];
      const valores = produtos.map((p) => linha.numero(p));
      const presentes = valores.filter((v) => v !== null);
      if (presentes.length < 2) return [];
      if (new Set(presentes).size < 2) return [];
      const alvo = linha.otimo === 'menor' ? Math.min(...presentes) : Math.max(...presentes);
      return valores.flatMap((v, i) => (v !== null && v === alvo ? [i] : []));
    }

    function linhaDivergente(linha, produtos) {
      if (produtos.length < 2) return false;
      return new Set(produtos.map((p) => linha.texto(p) ?? '—')).size > 1;
    }

    /* --------------------------------------------------------------- utilidades */

    const lacuna = '<span class="pc-cmp-lacuna">Não informado</span>';

    function foto(p, classe = '') {
      if (p && p.imagem) {
        return `<span class="pc-cmp-foto ${classe}"><img src="${escapar(p.imagem)}" alt="" loading="lazy" width="160" height="120"></span>`;
      }
      return `<span class="pc-cmp-foto pc-cmp-foto--vazia ${classe}" aria-hidden="true">${escapar((p && p.marca) || '')}</span>`;
    }

    function handlesDaUrl() {
      return PC.handlesDeParametro(new URLSearchParams(window.location.search).get('modelos'));
    }

    function urlPara(handles) {
      const busca = new URLSearchParams(window.location.search);
      if (handles.length > 0) busca.set('modelos', PC.parametroDeHandles(handles));
      else busca.delete('modelos');
      const texto = busca.toString().replace(/%2D/gi, '-');
      return `${window.location.pathname}${texto ? `?${texto}` : ''}`;
    }

    function navegar(handles, substituir = false) {
      // a URL muda antes da loja: o aviso "pc:comparar" já encontra as duas iguais
      const url = urlPara(handles);
      if (substituir) window.history.replaceState({}, '', url);
      else window.history.pushState({}, '', url);
      comparar.definir(handles);
      render();
    }

    /* ------------------------------------------------------------ seletor */

    let dialogo = null;
    function abrirSeletor(jaSelecionados, aoEscolher) {
      if (!dialogo) {
        dialogo = document.createElement('dialog');
        dialogo.className = 'pc-seletor';
        dialogo.setAttribute('aria-labelledby', 'pc-seletor-titulo');
        dialogo.innerHTML = `
          <div class="pc-seletor__caixa">
            <div class="pc-seletor__topo">
              <h2 class="pc-seletor__titulo" id="pc-seletor-titulo">Adicionar modelo à comparação</h2>
              <button type="button" class="pc-seletor__fechar" data-fechar aria-label="Fechar">${ICONE_X}</button>
            </div>
            <input type="search" class="pc-seletor__busca" placeholder="Buscar por modelo ou marca" aria-label="Buscar modelo" autocomplete="off">
            <ul class="pc-seletor__lista" role="list"></ul>
          </div>`;
        raiz.appendChild(dialogo);
        dialogo.addEventListener('click', (evento) => {
          if (evento.target === dialogo || evento.target.closest('[data-fechar]')) dialogo.close();
          const item = evento.target.closest('[data-escolher]');
          if (item) {
            dialogo.close();
            dialogo.aoEscolher(item.dataset.escolher);
          }
        });
        dialogo.querySelector('input').addEventListener('input', () => listarSeletor());
      }
      dialogo.jaSelecionados = jaSelecionados;
      dialogo.aoEscolher = aoEscolher;
      dialogo.querySelector('input').value = '';
      listarSeletor();
      dialogo.showModal();
    }

    function listarSeletor() {
      const busca = dialogo.querySelector('input').value.trim().toLowerCase();
      const lista = catalogo
        .filter((p) => !dialogo.jaSelecionados.includes(p.handle))
        .filter((p) => busca === '' || `${p.marca ?? ''} ${p.titulo} ${p.categoria ?? ''}`.toLowerCase().includes(busca))
        .slice(0, 40);
      dialogo.querySelector('ul').innerHTML =
        lista.length === 0
          ? '<li class="pc-seletor__vazio">Nenhum modelo encontrado.</li>'
          : lista
              .map(
                (p) => `
            <li>
              <button type="button" class="pc-seletor__item" data-escolher="${escapar(p.handle)}">
                ${foto(p, 'pc-cmp-foto--mini')}
                <span class="pc-seletor__nome">
                  <span class="pc-marca">${escapar(p.marca ?? '—')}</span>
                  <span class="pc-seletor__modelo">${escapar(p.titulo)}</span>
                </span>
                <span class="pc-seletor__preco">${formatMoeda(p.preco)}</span>
              </button>
            </li>`,
              )
              .join('');
    }

    /* ------------------------------------------------------ modo índice */

    function renderIndice() {
      document.title = TITULO_ORIGINAL;
      const handles = comparar.handles();
      if (slots) {
        slots.innerHTML = Array.from({ length: LIMITE }, (_, i) => {
          const handle = handles[i];
          if (!handle) {
            return `
              <li class="pc-comparar__slot">
                <span class="pc-comparar__slot-icone" aria-hidden="true">${PC.ICONE_COMPARAR}</span>
                <span class="pc-comparar__slot-rotulo">Espaço ${i + 1} vazio</span>
                <span class="pc-comparar__slot-dica">Marque “Comparar” em qualquer modelo do catálogo.</span>
              </li>`;
          }
          const p = catalogo.find((x) => x.handle === handle);
          return `
            <li class="pc-comparar__slot pc-comparar__slot--cheio">
              ${foto(p, 'pc-cmp-foto--slot')}
              <span class="pc-comparar__slot-nome">${escapar(p ? p.titulo : handle)}</span>
              <button type="button" class="pc-comparar__slot-remover" data-remover="${escapar(handle)}">Remover</button>
            </li>`;
        }).join('');
      }
      if (acoes) {
        acoes.querySelector('[data-ir]')?.remove();
        if (handles.length > 0) {
          acoes.insertAdjacentHTML(
            'beforeend',
            `<a class="pc-button" data-ir href="${escapar(comparar.link(handles))}">Comparar (${handles.length})</a>`,
          );
        }
      }
    }

    /* ------------------------------------------------ tabela (computador) */

    function celulaRitmo(p) {
      if (p.specs.ritmo_min === null || p.specs.ritmo_max === null) return lacuna;
      return reguaHTML(p.specs.ritmo_min, p.specs.ritmo_max, { escura: true, estimado: p.estimado });
    }

    function corpoTabela(produtos, vazios) {
      const linhas = LINHAS_TABELA.filter((linha) => !somenteDiferencas || linhaDivergente(linha, produtos));
      if (linhas.length === 0) {
        return `<tr><td colspan="${produtos.length + vazios + 1}" class="pc-cmp-tabela__nada">Estes modelos têm as mesmas informações em todas as linhas.</td></tr>`;
      }
      return linhas
        .map((linha) => {
          const destaques = melhoresIndices(linha, produtos);
          const divergente = linhaDivergente(linha, produtos);
          const celulas = produtos
            .map((p, indice) => {
              const destacada = destaques.includes(indice);
              const classe = destacada ? 'is-destaque' : divergente ? 'is-divergente' : '';
              const texto = linha.texto(p);
              const conteudo = linha.tipo === 'pacecard' ? celulaRitmo(p) : texto ? `<span>${escapar(texto)}</span>` : lacuna;
              const selo = destacada && linha.rotuloDestaque ? `<span class="pc-cmp-selo">${linha.rotuloDestaque}</span>` : '';
              return `<td class="${classe}">${conteudo}${selo}</td>`;
            })
            .join('');
          const vaziasHTML = Array.from({ length: vazios }, () => '<td class="is-vazia"></td>').join('');
          return `<tr><th scope="row">${linha.rotulo}</th>${celulas}${vaziasHTML}</tr>`;
        })
        .join('');
    }

    function tabelaHTML(produtos) {
      const vazios = Math.max(0, LIMITE - produtos.length);
      const cabecas = produtos
        .map(
          (p) => `
          <th scope="col" class="pc-cmp-tabela__modelo">
            <div class="pc-cmp-tabela__foto-linha">
              ${foto(p, 'pc-cmp-foto--tabela')}
              <button type="button" class="pc-cmp-remover" data-remover="${escapar(p.handle)}" aria-label="Remover ${escapar(p.titulo)} da comparação">${ICONE_X}</button>
            </div>
            <span class="pc-marca">${escapar(p.marca ?? 'Não informado')}</span>
            <a class="pc-cmp-tabela__nome" href="${escapar(p.url)}">${escapar(p.titulo)}</a>
            <span class="pc-cmp-tabela__preco">${formatMoeda(p.preco)}</span>
            <a class="pc-button pc-cmp-cta" href="${escapar(p.url)}">Ver ficha e escolher numeração</a>
          </th>`,
        )
        .join('');
      const cabecasVazias = Array.from(
        { length: vazios },
        () => `
          <th scope="col" class="pc-cmp-tabela__modelo">
            <button type="button" class="pc-cmp-adicionar" data-adicionar>
              <span aria-hidden="true">+</span>
              Adicionar modelo
            </button>
          </th>`,
      ).join('');
      return `
        <div class="pc-cmp-desk">
          <div class="pc-cmp-desk__topo">
            <label class="pc-cmp-check">
              <input type="checkbox" data-diferencas ${somenteDiferencas ? 'checked' : ''}>
              Mostrar só as diferenças
            </label>
            <span class="pc-cmp-contagem">${produtos.length} de ${LIMITE}</span>
          </div>
          <div class="pc-cmp-rolagem" tabindex="0" role="region" aria-label="Tabela comparativa; role horizontalmente para ver todas as colunas">
            <table class="pc-cmp-tabela">
              <caption class="pc-visually-hidden">Comparação técnica de ${produtos.length} tênis de corrida, linha a linha.</caption>
              <thead><tr><th scope="col" class="pc-cmp-tabela__canto">Especificação</th>${cabecas}${cabecasVazias}</tr></thead>
              <tbody data-corpo>${corpoTabela(produtos, vazios)}</tbody>
            </table>
          </div>
        </div>`;
    }

    /* ---------------------------------------------------------- celular */

    function vereditoHTML(produtos) {
      const precos = produtos.filter((p) => p.preco > 0);
      const pesos = produtos.filter((p) => typeof p.specs.peso_g === 'number');
      const frases = [];
      if (precos.length >= 2) {
        const ordenados = [...precos].sort((a, b) => a.preco - b.preco);
        const barato = ordenados[0];
        const caro = ordenados[ordenados.length - 1];
        const diferenca = caro.preco - barato.preco;
        if (diferenca > 0) frases.push(`${barato.titulo} sai ${formatMoeda(diferenca)} mais barato`);
      }
      if (pesos.length >= 2) {
        const leve = [...pesos].sort((a, b) => a.specs.peso_g - b.specs.peso_g)[0];
        if (pesos.some((p) => p.specs.peso_g !== leve.specs.peso_g)) {
          frases.push(`o ${leve.titulo} é o mais leve (${leve.specs.peso_g} g · tamanho de referência não informado)`);
        }
      }
      const ritmos = produtos
        .filter((p) => p.specs.ritmo_min !== null && p.specs.ritmo_max !== null)
        .map((p) => `${nomeCurto(p.titulo)} cobre ${formatRitmo(p.specs.ritmo_min)}–${formatRitmo(p.specs.ritmo_max)}`);
      if (frases.length === 0 && ritmos.length === 0) return '';
      let frase = frases.length === 2 ? `${frases[0]}, mas ${frases[1]}.` : frases.length === 1 ? `${frases[0]}.` : '';
      frase = frase.charAt(0).toUpperCase() + frase.slice(1);
      return `
        <section class="pc-cmp-veredito">
          <p class="pc-eyebrow">Em uma frase</p>
          ${frase ? `<p class="pc-cmp-veredito__frase">${escapar(frase)}</p>` : ''}
          ${ritmos.length > 0 ? `<p class="pc-cmp-veredito__ritmo">Ritmo: ${escapar(ritmos.join('; '))}.</p>` : ''}
        </section>`;
    }

    function graficoHTML(produtos) {
      const comRitmo = produtos
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => p.specs.ritmo_min !== null && p.specs.ritmo_max !== null);
      if (comRitmo.length === 0) return '';
      const pos = (v) => Math.min(100, Math.max(0, ((v - 3) / 4) * 100));
      const alturaBarra = 18;
      const espaco = 10;
      const altura = comRitmo.length * (alturaBarra + espaco) + 22;
      const marcas = [3, 4, 5, 6, 7];
      const linhas = marcas
        .map(
          (m) =>
            `<line x1="${pos(m)}" x2="${pos(m)}" y1="0" y2="${comRitmo.length * (alturaBarra + espaco)}" style="stroke:var(--pc-borda)" stroke-width="1" vector-effect="non-scaling-stroke"/>`,
        )
        .join('');
      const barras = comRitmo
        .map(({ p, i }, linha) => {
          const min = Math.min(p.specs.ritmo_min, p.specs.ritmo_max);
          const max = Math.max(p.specs.ritmo_min, p.specs.ritmo_max);
          const x = pos(min);
          const largura = Math.max(1, pos(max) - x);
          return `<rect x="${x}" y="${linha * (alturaBarra + espaco)}" width="${largura}" height="${alturaBarra}" rx="1" style="fill:${CORES_MODELO[i % 3]}"/>`;
        })
        .join('');
      const legenda = comRitmo
        .map(
          ({ p, i }) => `
          <li>
            <span class="pc-cmp-ponto" style="background:${CORES_MODELO[i % 3]}" aria-hidden="true"></span>
            <span class="pc-cmp-grafico__nome">${escapar(nomeCurto(p.titulo))}</span>
            <span class="pc-cmp-grafico__faixa">${formatRitmo(p.specs.ritmo_min)}–${formatRitmo(p.specs.ritmo_max)}/km${p.estimado ? ' · estimada' : ''}</span>
          </li>`,
        )
        .join('');
      return `
        <section class="pc-cmp-cartao">
          <p class="pc-eyebrow">Ritmo desejado</p>
          <svg class="pc-cmp-grafico" viewBox="0 0 100 ${altura}" preserveAspectRatio="none" style="height:${altura}px" aria-hidden="true">${linhas}${barras}</svg>
          <div class="pc-cmp-grafico__marcas" aria-hidden="true">${marcas.map((m) => `<span>${formatRitmo(m)}</span>`).join('')}</div>
          <ul class="pc-cmp-grafico__legenda" role="list">${legenda}</ul>
        </section>`;
    }

    function linhasMobileHTML(produtos) {
      const linhas = [...LINHAS_TABELA.filter((l) => l.tipo !== 'pacecard'), LINHA_CORES];
      const iguais = linhas.filter((l) => !linhaDivergente(l, produtos));
      const visiveis = soDiferencas ? linhas.filter((l) => linhaDivergente(l, produtos)) : linhas;
      const tres = produtos.length >= 3;
      const aviso =
        soDiferencas && iguais.length > 0
          ? `${iguais.length} linha${iguais.length === 1 ? '' : 's'} igua${iguais.length === 1 ? 'l' : 'is'} oculta${iguais.length === 1 ? '' : 's'}`
          : 'nada oculto';
      const secoes = visiveis
        .map((linha) => {
          const destaques = melhoresIndices(linha, produtos);
          if (!linhaDivergente(linha, produtos)) {
            const primeiro = linha.texto(produtos[0]);
            return `
              <section class="pc-cmp-linha">
                <p class="pc-eyebrow">${linha.rotulo}</p>
                <p class="pc-cmp-linha__igual"><span>${produtos.length > 1 ? `Igual nos ${produtos.length}: ` : ''}</span>${primeiro ? `<strong>${escapar(primeiro)}</strong>` : lacuna}</p>
              </section>`;
          }
          const valores = produtos
            .map((p, indice) => {
              const texto = linha.texto(p);
              const selo =
                destaques.includes(indice) && linha.rotuloDestaque
                  ? `<span class="pc-cmp-selo pc-cmp-selo--lima">${linha.rotuloDestaque}</span>`
                  : '';
              return `
                <div class="pc-cmp-linha__valor">
                  <span class="pc-cmp-linha__modelo" style="color:${CORES_MODELO[indice % 3]}">${escapar(nomeCurto(p.titulo))}</span>
                  <span class="pc-cmp-linha__texto">${texto ? escapar(texto) : lacuna}</span>
                  ${selo}
                </div>`;
            })
            .join('');
          return `
            <section class="pc-cmp-linha">
              <p class="pc-eyebrow">${linha.rotulo}</p>
              <div class="pc-cmp-linha__valores${tres ? ' pc-cmp-linha__valores--rolagem' : ''}"${tres ? ` tabindex="0" role="region" aria-label="${linha.rotulo}; role horizontalmente para comparar os modelos"` : ''}>${valores}</div>
            </section>`;
        })
        .join('');
      return { secoes, aviso };
    }

    function mobileHTML(produtos) {
      const { secoes, aviso } = linhasMobileHTML(produtos);
      const base = window.location.pathname;
      const modelos = produtos
        .map(
          (p, i) => `
          <li>
            ${foto(p, 'pc-cmp-foto--topo')}
            <span class="pc-cmp-mob__texto">
              <span class="pc-cmp-mob__nome" style="color:${CORES_MODELO[i % 3]}">${escapar(p.titulo)}</span>
              <span class="pc-cmp-mob__preco">${formatMoeda(p.preco)}</span>
            </span>
          </li>`,
        )
        .join('');
      const finais = produtos
        .map(
          (p) => `
          <div class="pc-cmp-final">
            <div class="pc-cmp-final__linha">
              <span class="pc-cmp-final__nome">${escapar(p.titulo)}</span>
              <span class="pc-cmp-final__preco">${formatMoeda(p.preco)}</span>
            </div>
            <a class="pc-button pc-cmp-cta" href="${escapar(p.url)}">Ver ficha e escolher numeração</a>
          </div>`,
        )
        .join('');
      return `
        <div class="pc-cmp-mob">
          <div class="pc-cmp-mob__topo">
            <div class="pc-cmp-mob__nav">
              <a href="${escapar(base)}" class="pc-cmp-mob__voltar"><span aria-hidden="true">←</span> Comparar</a>
              <a href="${escapar(base)}" class="pc-cmp-mob__editar">Editar seleção</a>
            </div>
            <ul class="pc-cmp-mob__modelos" role="list" style="grid-template-columns:repeat(${produtos.length},minmax(0,1fr))">${modelos}</ul>
          </div>
          <div class="pc-cmp-mob__corpo">
            ${vereditoHTML(produtos)}
            <div class="pc-cmp-mob__filtro">
              <button type="button" role="switch" aria-checked="${soDiferencas}" class="pc-cmp-switch" data-so-diferencas>
                <span class="pc-cmp-switch__trilho" aria-hidden="true"><span class="pc-cmp-switch__bola"></span></span>
                Só as diferenças
              </button>
              <span class="pc-cmp-contagem" data-oculto>${aviso}</span>
            </div>
            ${graficoHTML(produtos)}
            <div data-linhas-mob>${secoes}</div>
            <section class="pc-cmp-finais">${finais}</section>
          </div>
        </div>`;
    }

    /* ------------------------------------------------------ modo modelos */

    function produtosAtuais() {
      return handlesDaUrl()
        .map((h) => catalogo.find((p) => p.handle === h))
        .filter(Boolean);
    }

    function renderModelos() {
      const handles = handlesDaUrl();
      if (handles.join() !== comparar.handles().join()) comparar.definir(handles);
      document.title = `Comparação de ${handles.length} modelo${handles.length === 1 ? '' : 's'} · PaceCerto`;

      if (handles.length === 0) {
        resultado.innerHTML = `
          <div class="pc-cmp-vazio">
            <p>Nenhum modelo selecionado.</p>
            <a class="pc-button" href="${escapar(window.location.pathname)}">Montar comparação</a>
          </div>`;
        return;
      }
      if (!catalogoPronto) {
        resultado.innerHTML = erroCatalogo
          ? '<p class="pc-auxiliar">Não foi possível carregar o catálogo agora. Recarregue a página para tentar de novo.</p>'
          : '<p class="pc-auxiliar">Carregando modelos…</p>';
        return;
      }
      const produtos = produtosAtuais();
      if (produtos.length === 0) {
        resultado.innerHTML = `
          <div class="pc-cmp-vazio">
            <p>Não encontramos estes modelos no catálogo.</p>
            <a class="pc-button" href="${escapar(raiz.dataset.todos || '/collections/todos')}">Ver toda a linha</a>
          </div>`;
        return;
      }
      if (produtos.length !== handles.length) {
        // modelo que saiu do catálogo não fica preso na comparação
        navegar(
          produtos.map((p) => p.handle),
          true,
        );
        return;
      }
      resultado.innerHTML = mobileHTML(produtos) + tabelaHTML(produtos);
    }

    function render() {
      const comModelos = new URLSearchParams(window.location.search).has('modelos');
      raiz.classList.toggle('pc-comparar--modelos', comModelos);
      if (comModelos) renderModelos();
      else renderIndice();
      PC.atualizarBarra();
    }

    /* ---------------------------------------------------------- eventos */

    raiz.addEventListener('click', (evento) => {
      const remover = evento.target.closest('[data-remover]');
      if (remover) {
        const handle = remover.dataset.remover;
        if (raiz.classList.contains('pc-comparar--modelos')) navegar(handlesDaUrl().filter((h) => h !== handle));
        else comparar.remover(handle);
        return;
      }
      if (evento.target.closest('[data-adicionar]')) {
        const atuais = handlesDaUrl();
        abrirSeletor(atuais, (handle) => navegar([...atuais, handle]));
        return;
      }
      const alternar = evento.target.closest('[data-so-diferencas]');
      if (alternar) {
        soDiferencas = !soDiferencas;
        alternar.setAttribute('aria-checked', String(soDiferencas));
        const { secoes, aviso } = linhasMobileHTML(produtosAtuais());
        raiz.querySelector('[data-linhas-mob]').innerHTML = secoes;
        raiz.querySelector('[data-oculto]').textContent = aviso;
      }
    });

    raiz.addEventListener('change', (evento) => {
      if (!evento.target.matches('[data-diferencas]')) return;
      somenteDiferencas = evento.target.checked;
      const produtos = produtosAtuais();
      raiz.querySelector('[data-corpo]').innerHTML = corpoTabela(produtos, Math.max(0, LIMITE - produtos.length));
    });

    document.addEventListener('pc:comparar', () => {
      if (!raiz.classList.contains('pc-comparar--modelos')) {
        renderIndice();
        return;
      }
      const daLoja = comparar.handles();
      if (daLoja.join() !== handlesDaUrl().join()) navegar(daLoja, true);
    });

    window.addEventListener('popstate', render);

    render();
    PC.catalogo()
      .then((lista) => {
        catalogo = lista;
        catalogoPronto = true;
        render();
      })
      .catch(() => {
        erroCatalogo = true;
        render();
      });
  }

  if (window.PaceCerto) iniciar();
  else document.addEventListener('pc:pronto', iniciar, { once: true });
})();
